/*
 * What a person reads when a signup call fails: what went wrong and what to
 * do, never the backend's raw text ("failed to create account: not found").
 * By gRPC code first (the gateway sends some refusals as HTTP 500), then
 * HTTP status. A refusal the backend words for people keeps its words,
 * tidied; anything that looks internal is replaced.
 */
import { readApiError } from './apiError';

export { alreadyExists } from './apiError';

type Step = 'account' | 'verify' | 'org' | 'invite' | 'reset';

const INTERNAL = /failed to|rpc|grpc|sql|panic|unwrap|stack|::|\b[a-z]+_[a-z_]+\b|exception|status code/i;

const sentence = (s: string) => {
    const t = s.trim().replace(/\s+/g, ' ');
    if (!t) return t;
    const c = t.charAt(0).toUpperCase() + t.slice(1);
    return /[.!?]$/.test(c) ? c : `${c}.`;
};

const EXISTS: Record<Step, string> = {
    account: 'An account with this email already exists. Sign in instead, or use another email.',
    verify: 'This account is already verified. Sign in to continue.',
    org: 'That organization name is taken. Choose another one.',
    invite: 'You have already joined this organization. Sign in to continue.',
    reset: 'That didn\'t work. Ask for a new code and try again.',
};

export function errorText(e: unknown, step: Step, fallback = 'That didn\'t work. Try again.'): string {
    const { code, status, raw, requestId, unreachable } = readApiError(e);
    const ref = requestId ? ` If it keeps happening, contact Dodil support and quote ${requestId.slice(0, 8)}.` : '';
    const own = raw && !INTERNAL.test(raw) ? sentence(raw) : '';

    switch (code) {
        case 6: return EXISTS[step];
        case 3: case 11: return own || 'Some of what you entered isn\'t valid. Check it and try again.';
        // iam's one-organization limit (FAILED_PRECONDITION "organization limit reached").
        case 9: return /organization limit/i.test(raw)
            ? 'You already have an organization. Partners can create more.'
            : own || 'That can\'t be done right now.';
        case 5: return own || (step === 'verify' ? 'That code didn\'t match. Check it, or ask for a new one.' : fallback);
        case 7: case 16: return 'Your session has ended. Start again from the sign-up page.';
        // iam says how long to wait ("Wait 40 seconds…"); keep its words when it does.
        case 8: return own || 'Too many attempts just now. Wait a moment and try again.';
        // iam words a failed verification email for people ("we couldn't send the verification email just now…").
        case 14: return /verification email/i.test(raw) && own ? own : 'Dodil couldn\'t be reached just now. Try again in a moment.';
        case 4: return 'Dodil couldn\'t be reached just now. Try again in a moment.';
        case 2: case 12: case 13: case 15: return `Something went wrong on Dodil's side. Try again in a moment.${ref}`;
    }
    if (unreachable) return 'Dodil couldn\'t be reached. Check your connection and try again.';
    if (status === undefined) return fallback;
    if (status === 409) return EXISTS[step];
    if (status === 429) return 'Too many attempts just now. Wait a moment and try again.';
    if (status >= 502 && status <= 504) return 'Dodil couldn\'t be reached just now. Try again in a moment.';
    if (status >= 500) return `Something went wrong on Dodil's side. Try again in a moment.${ref}`;
    return own || fallback;
}

